/* eslint-disable @typescript-eslint/no-explicit-any */
import { IconFilter } from '@/styles/Icon'
import type { PropertyValue } from '@/types'
import { type Property } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { getPath } from '@/utils/next-util'
import { Popover, Transition } from '@headlessui/react'
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import ImageOptimize from '../ImageOptimize'
import { Loading } from '../Loading/Loading'
import { IconCloseB } from '../Popup/PopupFrame'
import { FilterItemGeneral } from './FilterItemGeneral'
interface FilterDataProps {
  name: string
  values: PropertyValue[] | string[]
  value: any
  data: PropertyValue[]
}
export interface FilterGeneral {
  properties: Property[]
  filterKey?: string
  onChange: () => void
  filterData: FilterDataProps[]
  setFilterData: Dispatch<SetStateAction<FilterDataProps[]>>
  fetchFilter: boolean
  setFetchFilter: Dispatch<SetStateAction<boolean>>
}
export const FilterGeneral = ({
  properties,
  onChange = () => true,
  filterData,
  setFilterData,
  fetchFilter,
  setFetchFilter,
}: FilterGeneral) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedValue, setSelectedValue] = useState<string[]>([])
  function clearFilter() {
    const EmptyArr = filterData.map(item => ({
      ...item,
      value: null,
      values: [],
    }))
    selectedFilterByKey([...EmptyArr])
    setFetchFilter(true)
    onChange()
  }
  function onSubmitFilter() {
    onChange()
    setFetchFilter(true)
  }
  const selectedFilterByKey = async (e: any[]) => {
    setIsLoading(true)
    const newFilter = filterData
    await updateValues(newFilter, e)
    setFilterData([...newFilter])
    setIsLoading(false)
    setFetchFilter(true)
  }
  const updateValues = async (
    oldArr: {
      name: string
      values: PropertyValue[] | string[]
      value: any
      data: PropertyValue[]
    }[],
    newArr: {
      name: string
      values: PropertyValue[] | string[]
      value: any
      data: PropertyValue[]
    }[]
  ) => {
    newArr.forEach(newObj => {
      const index = oldArr.findIndex(item => item.name === newObj.name)
      if (index !== -1) {
        if (newObj.values.length > 0) {
          oldArr[index].values = newObj.values
        } else {
          oldArr[index].values = []
        }
        if (newObj.values.length > 0) {
          oldArr[index].value = newObj.values.join(',')
        } else {
          oldArr[index].value = null
        }
      }
    })
  }
  useEffect(() => {
    const mergedValues: string[] = filterData.reduce<string[]>((acc, item) => {
      if (item.value !== null) {
        const itemValues = item.value
          .split(',')
          .map((val: string) => val.trim())
        acc.push(...itemValues)
      }
      return acc
    }, [])
    setSelectedValue([...mergedValues])
  }, [fetchFilter, filterData])

  const removeValue = (name: string, valueToDelete: any) => {
    const updatedFilters = filterData.map((filter: any) => {
      if (filter.name === name) {
        const updatedValues = filter.values.filter(
          (value: any) => value !== valueToDelete
        )
        return {
          ...filter,
          values: updatedValues,
        }
      }
      return filter
    })

    const index = updatedFilters.findIndex(filter => filter.name === name)
    if (index === -1) {
      return // Return or handle the case where the filter name is not found
    }
    selectedFilterByKey([...updatedFilters])
    setIsLoading(true)
    setFetchFilter(true)
  }

  return (
    <Popover as='div'>
      {({ open }) => (
        <div className='relative z-[30]'>
          <Popover.Button
            className={`relative h-10 w-full rounded-lg border border-dark-200 bg-white pl-2 pr-5  text-sm focus:outline-none focus-visible:border-primary-base ${
              open ? 'z-[9999]' : 'z-30'
            }`}
          >
            <div className='flex items-center gap-2'>
              <IconFilter />
              <span className='block truncate'>Bộ lọc</span>
            </div>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <span className='icon' aria-hidden='true'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={10}
                  height={8}
                  viewBox='0 0 10 8'
                  fill='none'
                >
                  <path
                    d='M4.68994 7.11426L9.23657 0.956238H0.143308L4.68994 7.11426Z'
                    fill='#6F6F6F'
                  />
                </svg>
              </span>
            </span>
          </Popover.Button>

          <Transition>
            <Transition.Child>
              <div className='fixed inset-0 left-0 top-0 bg-dark-900/40' />
            </Transition.Child>
            <Popover.Panel className='absolute left-0 z-[99] mt-[5px] w-screen max-w-[800px] overflow-hidden rounded-md bg-white text-sm'>
              <div className='relative max-h-[500px] overflow-y-auto p-3'>
                <Popover.Button className='absolute right-2 top-2 flex items-center gap-1'>
                  <IconCloseB />{' '}
                  <span className='text-sm font-normal leading-normal text-dark-800'>
                    Đóng
                  </span>
                </Popover.Button>

                <div className='mb-2 w-[calc(100%-60px)]'>
                  {selectedValue && selectedValue.length > 0 ? (
                    <div className='filterSelected flex w-full items-center gap-3'>
                      <div className='max-w-[80px] flex-[80px]  text-sm font-bold text-dark-800'>
                        Đã chọn:
                      </div>
                      <div className='listItem flex flex-1 flex-wrap gap-1 text-sm'>
                        {properties.map(keyFilter => {
                          return keyFilter.values.map((item, index) => {
                            const translatedValue = getTranslate(item.value)

                            if (selectedValue.includes(translatedValue)) {
                              return (
                                <button
                                  className='relative flex items-center rounded border border-dark-200 bg-white pl-2.5 pr-4 outline-none'
                                  key={index}
                                  onClick={() =>
                                    removeValue(
                                      getPath(keyFilter.slugs),
                                      getTranslate(item.value)
                                    )
                                  }
                                >
                                  {item.image ? (
                                    <ImageOptimize
                                      src={item?.image}
                                      alt={getTranslate(item.value)}
                                      width={120}
                                      height={20}
                                      className='h-4 w-auto'
                                    />
                                  ) : (
                                    <span className='py-[2px]'>
                                      {getTranslate(item.value)}
                                    </span>
                                  )}

                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width={8}
                                    height={8}
                                    viewBox='0 0 8 8'
                                    fill='none'
                                    className='absolute right-2.5 top-[50%] z-10 translate-y-[-50%]'
                                  >
                                    <path
                                      d='M4.57808 3.99948L7.50776 0.507289C7.55687 0.449254 7.51558 0.361084 7.43968 0.361084H6.54906C6.4966 0.361084 6.44638 0.384521 6.41178 0.4247L3.99549 3.30528L1.57919 0.4247C1.54571 0.384521 1.49549 0.361084 1.44191 0.361084H0.551289C0.475396 0.361084 0.434102 0.449254 0.483209 0.507289L3.4129 3.99948L0.483209 7.49167C0.472208 7.5046 0.465151 7.52042 0.462875 7.53725C0.460598 7.55408 0.463198 7.5712 0.470367 7.5866C0.477535 7.60199 0.48897 7.61501 0.503314 7.62409C0.517658 7.63318 0.534309 7.63796 0.551289 7.63787H1.44191C1.49437 7.63787 1.54459 7.61443 1.57919 7.57425L3.99549 4.69367L6.41178 7.57425C6.44526 7.61443 6.49549 7.63787 6.54906 7.63787H7.43968C7.51558 7.63787 7.55687 7.5497 7.50776 7.49167L4.57808 3.99948Z'
                                      fill='black'
                                      fillOpacity='0.45'
                                    />
                                  </svg>
                                </button>
                              )
                            } else {
                              return null
                            }
                          })
                        })}
                        <button
                          className='clearAllFilter  text-sm font-medium text-primary-base'
                          onClick={clearFilter}
                        >
                          Xóa tất cả
                        </button>
                      </div>
                    </div>
                  ) : (
                    ''
                  )}
                </div>

                <div className='relative grid grid-cols-1 gap-3'>
                  {properties &&
                    properties
                      .filter(item => item.slugs?.postfix == '-brand')
                      .map((e: Property) => (
                        <FilterItemGeneral
                          key={e._id}
                          filterGroup={e}
                          filterKey={getPath(e.slugs)}
                          selectedFilterByKey={selectedFilterByKey}
                          className='mb-3'
                          filterData={filterData}
                          fetchFilter={fetchFilter}
                          setFetchFilter={setFetchFilter}
                          setFilterData={setFilterData}
                          title={getTranslate(e.displayName)}
                        />
                      ))}
                </div>
                <div className='relative grid grid-cols-3 gap-3'>
                  {properties &&
                    properties
                      .filter(item => item.slugs?.postfix != '-brand')
                      .map((e: Property) => (
                        <FilterItemGeneral
                          key={e._id}
                          filterGroup={e}
                          filterKey={getPath(e.slugs)}
                          selectedFilterByKey={selectedFilterByKey}
                          className='mb-1.5'
                          filterData={filterData}
                          fetchFilter={fetchFilter}
                          setFetchFilter={setFetchFilter}
                          setFilterData={setFilterData}
                          title={getTranslate(e.displayName)}
                        />
                      ))}
                </div>
              </div>
              <div className='actionFilter flex w-full justify-center gap-3 pb-3 pt-1 shadow'>
                <Popover.Button
                  onClick={clearFilter}
                  className='flex h-9 items-center rounded border border-primary-second px-4 text-center text-sm font-bold uppercase text-primary-second'
                >
                  BỎ CHỌN
                </Popover.Button>

                <Popover.Button
                  onClick={() => onSubmitFilter()}
                  className={`flex h-9 items-center rounded bg-red-600 px-4 text-center text-sm font-bold uppercase text-white `}
                >
                  {isLoading ? <Loading /> : ''}
                  XÁC NHẬN
                </Popover.Button>
              </div>
            </Popover.Panel>
          </Transition>
        </div>
      )}
    </Popover>
  )
}
